// Drink logger — ONE TAP = ONE DRINK LOGGED, canon flow.
// Ported from design-canon/project/src/screens-logger.jsx → DrinkLoggerScreen.
//
// Layout (top → bottom):
//   1. TopBar — "logging · stop N" eyebrow + map icon to live board
//   2. VenueBanner (tappable → /night/$id/venue) — "now at THE LOCAL · …"
//   3. Caveat hand prompt — "finished one?  tap it."
//   4. DrinkTile 2×2 grid — beer / wine / cocktail / shot
//   5. EmptiesStrip — horizontal pile of completed glasses + count chip
//
// The in-stumble action row (NEXT STOP · camera · END NIGHT) lives in the
// global BottomNav contextual variant (F-Wave PR α / F8) — see BottomNav.tsx.
// Camera + End slots dispatch DOM custom events that this route listens for.
//
// Drink tap flow:
//   - Optimistic count bump on tap (count++; rollback on POST failure)
//   - DrinkTile fires onLog mid-animation → POST /api/nights/:id/drinks
//   - After success: dispatch `stumble:drink-logged` event so the SelfieSheet
//     (or any other listener) can auto-prompt for proof.
//
// Routes:
//   GET /api/nights/:id         → load details
//   POST /api/nights/:id/drinks → log a drink (optimistic)
//   POST /api/nights/:id/end    → end the night

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { type DrinkDto, type NightDetailsDto, nightApi } from '../../../api'
import { OPEN_CAMERA_EVENT, OPEN_END_NIGHT_EVENT } from '../../../components/BottomNav'
import { DrinkTile, type DrinkType } from '../../../components/DrinkTile'
import { EmptiesStrip } from '../../../components/EmptiesStrip'
import { Icon } from '../../../components/Icon'
import { SelfieSheet } from '../../../components/SelfieSheet'
import { BackButton, TopBar } from '../../../components/TopBar'
import { VenueBanner } from '../../../components/VenueBanner'
import { track } from '../../../lib/analytics'
import {
  MEDIA_FAILED_EVENT,
  MEDIA_SAVED_EVENT,
  STORAGE_FULL_EVENT,
} from '../../../lib/media/uploadQueue'

const DRINK_TYPES: readonly DrinkType[] = ['beer', 'wine', 'cocktail', 'shot']

const VENUE_TINTS = ['var(--orange)', 'var(--pink)', 'var(--teal)', 'var(--lime)']

export const Route = createFileRoute('/night/$id/')({
  component: DrinkLoggerRoute,
})

/** Public event name emitted right after a successful drink POST. */
export const DRINK_LOGGED_EVENT = 'stumble:drink-logged'
export type DrinkLoggedEventDetail = { type: DrinkType; nightId: string }

function DrinkLoggerRoute() {
  const { id: nightId } = Route.useParams()
  const navigate = useNavigate()
  const [details, setDetails] = useState<NightDetailsDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [endOpen, setEndOpen] = useState(false)
  const [selfieOpen, setSelfieOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  // Optimistic +1s by type — added on tap, removed when server response lands.
  const [optimistic, setOptimistic] = useState<Record<DrinkType, number>>({
    beer: 0,
    wine: 0,
    cocktail: 0,
    shot: 0,
  })
  const lastTapAt = useRef(0)

  async function load() {
    setLoading(true)
    try {
      const res = await nightApi.get(nightId)
      setDetails(res.night)
      // No venue yet? Send to check-in.
      if (!res.night.currentVenue) {
        navigate({ to: '/night/$id/venue', params: { id: nightId } })
      }
    } catch {
      setError("Couldn't load this stumble.")
    } finally {
      setLoading(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch only when route param nightId changes; load/navigate are stable.
  useEffect(() => {
    void load()
  }, [nightId])

  // F8: listen for the global in-stumble BottomNav events. The nav is mounted
  // at the root level and can't reach into this route's modal state, so it
  // dispatches DOM custom events that we map back to local setState.
  useEffect(() => {
    function onOpenCamera() {
      setSelfieOpen(true)
    }
    function onOpenEndNight() {
      setEndOpen(true)
    }
    window.addEventListener(OPEN_CAMERA_EVENT, onOpenCamera)
    window.addEventListener(OPEN_END_NIGHT_EVENT, onOpenEndNight)
    return () => {
      window.removeEventListener(OPEN_CAMERA_EVENT, onOpenCamera)
      window.removeEventListener(OPEN_END_NIGHT_EVENT, onOpenEndNight)
    }
  }, [])

  // F2: react to background upload events. The SelfieSheet now closes
  // instantly on KEEP and the upload runs out-of-band via the IndexedDB
  // queue. We refresh details + toast on success/failure so the user gets
  // confirmation even though the sheet is long gone.
  // biome-ignore lint/correctness/useExhaustiveDependencies: load + showToast are stable; only nightId matters.
  useEffect(() => {
    function onSaved(e: Event) {
      const detail = (e as CustomEvent<{ nightId: string; mediaId: string }>).detail
      if (detail?.nightId && detail.nightId !== nightId) return
      void load()
      track('selfie_uploaded', { nightId, mediaId: detail?.mediaId ?? null })
      showToast('✓ saved')
    }
    function onFailed(e: Event) {
      const detail = (e as CustomEvent<{ nightId: string }>).detail
      if (detail?.nightId && detail.nightId !== nightId) return
      showToast('⚠ photo failed — tap camera to retry')
    }
    function onStorageFull() {
      showToast('storage full — photo uploading now')
    }
    window.addEventListener(MEDIA_SAVED_EVENT, onSaved)
    window.addEventListener(MEDIA_FAILED_EVENT, onFailed)
    window.addEventListener(STORAGE_FULL_EVENT, onStorageFull)
    return () => {
      window.removeEventListener(MEDIA_SAVED_EVENT, onSaved)
      window.removeEventListener(MEDIA_FAILED_EVENT, onFailed)
      window.removeEventListener(STORAGE_FULL_EVENT, onStorageFull)
    }
  }, [nightId])

  async function logDrink(type: DrinkType) {
    // 250ms debounce as a safety net — DrinkTile already animates ~1.2s per tap.
    const now = Date.now()
    if (now - lastTapAt.current < 250) return
    lastTapAt.current = now

    // Optimistic +1 — count flips before the network round-trip.
    setOptimistic((o) => ({ ...o, [type]: o[type] + 1 }))

    try {
      const res = await nightApi.logDrink(nightId, { drinkType: type })
      setDetails(res.night)
      // Server-confirmed — drop the optimistic overlay for this type.
      setOptimistic((o) => ({ ...o, [type]: Math.max(0, o[type] - 1) }))
      track('drink_logged', { drinkType: type, nightId })
      showToast(`+1 ${type}`)
      // Notify the selfie sheet (and any other listener) that a drink landed.
      if (typeof window !== 'undefined') {
        const detail: DrinkLoggedEventDetail = { type, nightId }
        window.dispatchEvent(new CustomEvent(DRINK_LOGGED_EVENT, { detail }))
      }
    } catch {
      // Rollback the optimistic +1.
      setOptimistic((o) => ({ ...o, [type]: Math.max(0, o[type] - 1) }))
      showToast("couldn't save — try again")
    }
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 1600)
  }

  async function endNight() {
    setBusy(true)
    try {
      await nightApi.end(nightId)
      track('night_ended', {
        nightId,
        totalDrinks: details?.totals.drinks ?? 0,
        stops: details?.sessionVenues.length ?? 0,
      })
      navigate({ to: '/night/$id/recap', params: { id: nightId } })
    } catch {
      setError("Couldn't end the night. Try again.")
    } finally {
      setBusy(false)
      setEndOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="stumble-screen">
        <TopBar left={<BackButton onClick={() => navigate({ to: '/' })} />} eyebrow="loading" />
        <div
          className="screen-body body muted"
          style={{ alignContent: 'center', textAlign: 'center' }}
        >
          loading your stumble…
        </div>
      </div>
    )
  }
  if (!details) {
    return (
      <div className="stumble-screen">
        <TopBar left={<BackButton onClick={() => navigate({ to: '/' })} />} eyebrow="error" />
        <div className="screen-body body" style={{ textAlign: 'center', padding: 20 }}>
          {error ?? 'Something went wrong.'}
          <button type="button" className="btn full" onClick={load} style={{ marginTop: 16 }}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  const byType = details.totals.byType ?? {}
  // Effective per-type count = server count + optimistic adds.
  function effectiveCount(type: DrinkType): number {
    return (byType[type] ?? 0) + optimistic[type]
  }
  // Effective total drinks tonight = server total + optimistic adds.
  const optimisticTotal = optimistic.beer + optimistic.wine + optimistic.cocktail + optimistic.shot
  const totalDrinks = details.totals.drinks + optimisticTotal
  const venueName = details.currentVenue?.venue.name ?? 'somewhere'
  const venueSubtitle = details.currentVenue?.venue.city ?? null

  // Drinks at the CURRENT venue (for VenueBanner "N here").
  const currentVenueId = details.currentVenue?.venueId
  const hereCount =
    (currentVenueId
      ? details.drinks.filter((d) => d.venueId === currentVenueId).length
      : details.drinks.length) + optimisticTotal

  // Build the optimistic empties list. Real drinks first, then ephemeral
  // placeholders for any optimistic +1s waiting on the server.
  const empties: DrinkDto[] = buildOptimisticEmpties(details, optimistic, nightId)

  // Tint for the venue banner — stable per venue id.
  const tint = currentVenueId
    ? VENUE_TINTS[hashIndex(currentVenueId, VENUE_TINTS.length)]
    : VENUE_TINTS[0]

  // Stop index for the eyebrow ("stop 2") — visiting a venue at least once.
  const stopIndex = Math.max(1, details.sessionVenues.length)

  return (
    <div className="stumble-screen">
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/' })} />}
        eyebrow={`logging · stop ${stopIndex}`}
        title={`${totalDrinks} ${totalDrinks === 1 ? 'drink' : 'drinks'}`}
        right={
          // F9: top-right slot now opens the live friends board (map icon)
          // instead of End Night. END NIGHT is reachable from the in-stumble
          // BottomNav (F8), so no functionality is lost.
          <button
            type="button"
            className="icon-btn"
            onClick={() => navigate({ to: '/stumbling' })}
            aria-label="See friends' live board"
            data-testid="open-live-board"
          >
            <Icon name="map" size={20} />
          </button>
        }
      />
      {/* F8: grid is now venue · prompt · tiles · empties (4 rows). The old
          5th row (NEXT STOP · selfie · END NIGHT action row) is gone — those
          actions live in the new in-stumble BottomNav. */}
      <div
        className="screen-body"
        style={{ gridTemplateRows: 'auto auto 1fr auto', gap: 12, padding: '4px 16px 16px' }}
      >
        {/* F8: tap the venue banner to jump to /night/$id/venue (next stop).
            Path preserved after the action row was removed. */}
        <button
          type="button"
          onClick={() => navigate({ to: '/night/$id/venue', params: { id: nightId } })}
          data-testid="venue-banner-tap"
          aria-label={`Change venue — currently at ${venueName}`}
          style={{
            display: 'block',
            padding: 0,
            border: 'none',
            background: 'transparent',
            textAlign: 'left',
            width: '100%',
            cursor: 'pointer',
            minHeight: 'revert',
          }}
        >
          <VenueBanner
            name={venueName}
            subtitle={venueSubtitle}
            hereCount={hereCount}
            tonightCount={totalDrinks}
            tint={tint}
          />
        </button>

        {totalDrinks > 0 ? (
          <div className="logger-prompt">
            <span className="hand" style={{ fontSize: 22, color: 'var(--pink)' }}>
              finished one?
            </span>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 20,
                lineHeight: 1,
                textTransform: 'lowercase',
              }}
            >
              tap it.
            </span>
          </div>
        ) : (
          <div className="logger-prompt">
            <span className="hand" style={{ fontSize: 22, color: 'var(--pink)' }}>
              kick it off
            </span>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 20,
                lineHeight: 1,
                textTransform: 'lowercase',
              }}
            >
              tap a glass.
            </span>
          </div>
        )}

        <div className="drink-tile-grid">
          {DRINK_TYPES.map((type) => (
            <DrinkTile
              key={type}
              type={type}
              count={effectiveCount(type)}
              onLog={() => void logDrink(type)}
            />
          ))}
        </div>

        <EmptiesStrip drinks={empties} />
      </div>

      {toast && <div className="toast">{toast}</div>}

      <SelfieSheet
        open={selfieOpen}
        nightId={nightId}
        venueName={details.currentVenue?.venue.name}
        city={details.currentVenue?.venue.city ?? undefined}
        onClose={() => setSelfieOpen(false)}
        // Auto-trigger after each drink-logged event (60s cooldown after a
        // dismissal). The sheet listens for the same DRINK_LOGGED_EVENT this
        // route dispatches and asks us to flip our open state so we stay the
        // single source of truth.
        onAutoOpen={() => setSelfieOpen(true)}
        onUploaded={(media) => {
          // F2: the actual server-side MediaDto arrives later via
          // MEDIA_SAVED_EVENT (queued upload). This onUploaded fires
          // immediately when KEEP is tapped — refresh + toast so the user
          // sees their +1 selfie placeholder right away. The async path
          // re-refreshes on MEDIA_SAVED_EVENT.
          void load()
          showToast('selfie queued')
          // `media` is undefined in the queue path, defined only if a future
          // sync caller wants it.
          void media?.id
        }}
      />

      {endOpen && (
        <div
          className="sheet-scrim"
          onClick={() => setEndOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEndOpen(false)
          }}
          role="presentation"
        >
          <div
            className="sheet"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            // biome-ignore lint/a11y/useSemanticElements: see SelfieSheet.tsx —
            // bottom-sheet doesn't use native <dialog>.
            role="dialog"
            aria-modal="true"
          >
            <div className="grip" />
            <div>
              <span className="eyebrow">end the night?</span>
              <h2 className="h-md">that's a wrap.</h2>
              <p className="body" style={{ marginTop: 4 }}>
                {totalDrinks} {totalDrinks === 1 ? 'drink' : 'drinks'} at {venueName}.
              </p>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <button
                type="button"
                className="btn secondary full"
                onClick={() => setEndOpen(false)}
                disabled={busy}
              >
                Keep going
              </button>
              <button
                type="button"
                className="btn danger full"
                onClick={endNight}
                disabled={busy}
                data-testid="confirm-end-night"
              >
                {busy ? 'ending…' : 'End night'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** Build the empties list shown in the strip, including optimistic placeholders. */
function buildOptimisticEmpties(
  details: NightDetailsDto,
  optimistic: Record<DrinkType, number>,
  nightId: string,
): DrinkDto[] {
  // Sort confirmed drinks oldest → newest so the strip's auto-scroll lands on
  // the most-recent at the right edge.
  const real = [...details.drinks].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )
  const placeholders: DrinkDto[] = []
  const now = new Date().toISOString()
  for (const type of DRINK_TYPES) {
    for (let i = 0; i < optimistic[type]; i += 1) {
      placeholders.push({
        id: `optimistic-${type}-${i}`,
        nightSessionId: nightId,
        sessionVenueId: details.currentVenue?.id ?? '',
        userId: details.night.userId,
        venueId: details.currentVenue?.venueId ?? '',
        drinkType: type,
        drinkName: null,
        createdAt: now,
      })
    }
  }
  return [...real, ...placeholders]
}

/** Stable index into a fixed array, given a string key. */
function hashIndex(key: string, mod: number): number {
  let h = 0
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0
  return Math.abs(h) % mod
}
